import { useState, type ComponentType, type JSX, type SVGProps } from 'react';
import { Volume2 as LucideVolume2, Volume1 as LucideVolume1, VolumeX as LucideVolumeX } from 'lucide-react';
import { useAudio } from '../../audio/useAudio';

// P7-5: HUD の絵文字廃止。シェルは lucide-react を直接 import する流儀（RouteMapButton踏襲）。
type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;
const Volume2 = LucideVolume2 as unknown as IconComponent;
const Volume1 = LucideVolume1 as unknown as IconComponent;
const VolumeX = LucideVolumeX as unknown as IconComponent;

/**
 * グローバル音量/ミュート UI（R2 感覚層・Wave2A）。
 *
 * 本番プレイ画面に常設するコンパクトなトグル。useAudio() の単一ソースに直接バインドし、
 * BGM・SFX・heartbeat・アンビエントすべて（master gain 経由）を一括でミュート/音量調整する。
 * 既定は「音あり」（muted=false / volume=0.6）。ユーザーはいつでもミュートできる。
 *
 * 配置・流儀は既存の右上ボタン群（現在地マップ等）に馴染ませる。内部用語は出さない。
 * アイコンは P7-5 で絵文字（🔊/🔉/🔇）から lucide-react へ置換（HUD全体の視覚統一）。
 * 既定はトグルのみ。ボタンにマウス/フォーカスが乗ったときだけ音量スライダーを展開し、
 * 通常時は場面の邪魔をしない。
 */
export function GlobalAudioControl(): JSX.Element {
  const { muted, volume, toggleMuted, setVolume } = useAudio();
  const [expanded, setExpanded] = useState(false);

  const Icon = muted || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;

  return (
    <div
      className="global-audio-control"
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      onFocus={() => setExpanded(true)}
      onBlur={() => setExpanded(false)}
    >
      <button
        type="button"
        className="global-audio-control__btn"
        onClick={toggleMuted}
        aria-label={muted ? '音を出す' : '音を消す'}
        aria-pressed={muted}
      >
        <Icon className="global-audio-control__icon" aria-hidden="true" />
      </button>
      <input
        type="range"
        className={`global-audio-control__slider${expanded ? ' is-expanded' : ''}`}
        min="0"
        max="1"
        step="0.05"
        value={muted ? 0 : volume}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          setVolume(v);
          // スライダーで音量を上げたらミュート解除（直感に合わせる）
          if (v > 0 && muted) toggleMuted();
        }}
        aria-label="音量"
        tabIndex={expanded ? 0 : -1}
      />
    </div>
  );
}

export default GlobalAudioControl;
